/**
 * A caption is three things the host edits separately: the words, the tracked link and the
 * hashtags. The stored caption is one string (that is what the platforms get), so the editor
 * splits it on the way in and joins it on the way out; nothing here invents text.
 */
export type CaptionParts = {
  body: string;
  /** The tracked short URL as it appears in the caption, or null when there is none. */
  link: string | null;
  hashtags: string[];
};

const LINK = /\S+\/l\/[A-Za-z0-9-]+/;
const LINK_LINE = /^(?:Book direct: |Check your dates: )?(\S+\/l\/[A-Za-z0-9-]+)$/;
const HASHTAG_LINE = /^(#[A-Za-z0-9_]+\s*)+$/;

export function splitCaption(caption: string): CaptionParts {
  const lines = caption.replace(/\r\n/g, '\n').trim().split('\n');
  const hashtags: string[] = [];
  while (lines.length) {
    const last = lines[lines.length - 1]!.trim();
    if (!last) {
      lines.pop();
      continue;
    }
    if (!HASHTAG_LINE.test(last)) break;
    hashtags.unshift(...last.split(/\s+/).map((t) => t.replace(/^#/, '')));
    lines.pop();
  }
  let link: string | null = null;
  const kept: string[] = [];
  for (const line of lines) {
    const m: RegExpExecArray | null = link ? null : LINK_LINE.exec(line.trim());
    if (m) link = m[1]!;
    else kept.push(line);
  }
  const body = kept.join('\n').trim();
  if (!link) link = LINK.exec(body)?.[0] ?? null;
  return { body, link, hashtags };
}

/** Rewrites an inline link inside the words: swapped for the new one, or removed with its joiner. */
function relink(body: string, next: string | null): string {
  const current = LINK.exec(body)?.[0];
  if (!current) return body;
  if (next) return body.replace(current, next);
  return body
    .replace(new RegExp(`\\s+(?:at|:)\\s+${escape(current)}`), '')
    .replace(new RegExp(`\\s*${escape(current)}`), '')
    .replace(/\s+([.!?])/g, '$1')
    .trim();
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function joinCaption(parts: CaptionParts): string {
  const inline = LINK.test(parts.body);
  const body = inline ? relink(parts.body, parts.link) : parts.body.trim();
  const out = [body];
  if (parts.link && !inline) out[0] = `${body}\n${parts.link}`;
  const tags = parts.hashtags.map((h) => h.trim().replace(/^#/, '')).filter(Boolean);
  if (tags.length) out.push(tags.map((h) => `#${h}`).join(' '));
  return out.filter(Boolean).join('\n\n');
}

const TITLE_MAX = 80;

/**
 * A post's name from the host's own words: the first sentence of the caption (its first line,
 * up to the first full stop, without the link line or hashtags), kept to eighty characters on a
 * word. Empty when the caption has no words. A post is never named after a generation brief.
 */
export function captionTitle(caption: string | null | undefined): string {
  const line = splitCaption(caption ?? '')
    .body.split('\n')
    .map((l) => l.trim())
    .find(Boolean);
  if (!line) return '';
  const sentence = (/^.*?[.!?](?=\s|$)/.exec(line)?.[0] ?? line).replace(/\.$/, '').trim();
  if (sentence.length <= TITLE_MAX) return sentence;
  const cut = sentence.slice(0, TITLE_MAX);
  const space = cut.lastIndexOf(' ');
  return `${(space > 40 ? cut.slice(0, space) : cut).replace(/[,;:\s]+$/, '')}…`;
}
